// ── Inline "Add New Lab Facility" Form ──

import { useState, useEffect } from 'react'
import type { Organization } from '@medplum/fhirtypes'
import { Button, Group, InputBase, Select, Stack, Text, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { IMaskInput } from 'react-imask'
import { useMedplum } from '@medplum/react'
import { config } from '@/config'
import { useMiddlewareClient } from '@/hooks'
import { useDebounce } from '@/hooks/useDebounce'
import { states } from '@/utils/states.ts'
import { logError } from './errorUtils'

type ParentLabName = 'Labcorp' | 'Quest Diagnostics'

interface AddFacilityFormValues {
  name: string
  street: string
  city: string
  state: string
  zip: string
  fax: string
  phone: string
}

export type NewFacilityData = {
  name: string
  address: string
  city: string
  state: string
  zip: string
  fax: string
}

export function InlineAddLabFacilityForm({
  parentLabName,
  onSave,
  onCancel,
}: {
  parentLabName: ParentLabName
  onSave: (orgId: string, data: NewFacilityData) => void
  onCancel: () => void
}) {
  const medplum = useMedplum()
  const middleware = useMiddlewareClient()
  const [saving, setSaving] = useState(false)
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null)

  const form = useForm<AddFacilityFormValues>({
    initialValues: {
      name: '',
      street: '',
      city: '',
      state: '',
      zip: '',
      fax: '',
      phone: '',
    },
    validate: {
      name: (v) => (!v.trim() ? 'Facility name is required' : null),
      street: (v) => (!v.trim() ? 'Street address is required' : null),
      city: (v) => (!v.trim() ? 'City is required' : null),
      state: (v) => (!v.trim() ? 'State is required' : null),
      zip: (v) => {
        if (!v.trim()) return 'ZIP code is required'
        const digits = v.replace(/\D/g, '')
        return digits.length === 5 || digits.length === 9 ? null : 'ZIP code must be 5 or 9 digits'
      },
      // PD-1929 — required: a lab order can't advance to a facility without a fax,
      // so a fax-less facility added here would dead-end the order step.
      fax: (v) => {
        const digits = v.replace(/\D/g, '')
        if (!digits) return 'Fax number is required'
        return digits.length === 10 ? null : 'Invalid fax number'
      },
      phone: (v) => {
        if (!v) return null
        const digits = v.replace(/\D/g, '')
        return digits.length === 10 ? null : 'Invalid phone number'
      },
    },
  })

  const debouncedName = useDebounce(form.values.name, 500)

  useEffect(() => {
    const name = debouncedName.trim()
    if (name.length < 3) {
      setDuplicateWarning(null)
      return
    }

    let cancelled = false

    medplum
      .search(
        'Organization',
        `type=laboratory-location&name:contains=${encodeURIComponent(name)}&partof.name=${encodeURIComponent(parentLabName)}&_count=5`,
      )
      .then((result) => {
        if (cancelled) return
        const matches = (result.entry?.map((e) => e.resource as Organization) ?? []).filter(Boolean)
        if (matches.length > 0) {
          const names = matches
            .slice(0, 3)
            .map((m) => `"${m.name}"`)
            .join(', ')
          setDuplicateWarning(
            `Similar facilit${matches.length === 1 ? 'y' : 'ies'} already exist: ${names}${matches.length > 3 ? ` and ${matches.length - 3} more` : ''}. Please verify this is not a duplicate.`,
          )
        } else {
          setDuplicateWarning(null)
        }
      })
      .catch((err) => {
        logError('Duplicate check', err)
        if (!cancelled) setDuplicateWarning(null)
      })

    return () => {
      cancelled = true
    }
  }, [debouncedName, medplum, parentLabName])

  const handleSubmit = async (values: AddFacilityFormValues) => {
    setSaving(true)
    try {
      const zip = values.zip

      // Geocode address (best-effort)
      let coords: { lat: number; lon: number } | null = null
      const fullAddress = [values.street, values.city, values.state, zip].filter(Boolean).join(', ')
      const token = medplum.getAccessToken()
      const baseUrl = config.middlewareUrl || ''

      if (baseUrl) {
        try {
          const resp = await fetch(
            `${baseUrl}/order/geocode?q=${encodeURIComponent(fullAddress)}`,
            {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            },
          )
          if (resp.ok) {
            coords = (await resp.json()) as { lat: number; lon: number } | null
          }
        } catch {
          // Geocoding is best-effort
        }
      }

      const faxDigits = values.fax.replace(/\D/g, '')

      const created = await middleware.post('/order/create-lab-facility', {
        parentLabName,
        name: values.name,
        street: values.street,
        city: values.city,
        state: values.state,
        zip,
        ...(faxDigits ? { fax: values.fax } : {}),
        ...(values.phone.trim() ? { phone: values.phone } : {}),
        ...(coords?.lat != null && coords?.lon != null ? { lat: coords.lat, lon: coords.lon } : {}),
      })

      if (created?.id) {
        medplum.invalidateSearches('Organization')
        onSave(created.id, {
          name: values.name,
          address: values.street,
          city: values.city,
          state: values.state,
          zip,
          fax: values.fax,
        })
      } else {
        throw new Error('Unexpected response from server — no facility ID returned')
      }
    } catch (err) {
      logError('Facility create', err)
      notifications.show({
        title: 'Error',
        message: `Failed to create ${parentLabName} facility. Please try again.`,
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <Text fw={600} size="lg">
          Add New {parentLabName} Facility
        </Text>
        <Text size="xs" c="dimmed">
          New facilities require admin approval before orders can be faxed. You can save a draft
          order in the meantime.
        </Text>

        <TextInput
          label="Location Name"
          placeholder="e.g. Abbeville (Ozark Rd)"
          {...form.getInputProps('name')}
          required
        />
        {duplicateWarning && (
          <Text size="xs" c="orange" mt={-8}>
            {duplicateWarning}
          </Text>
        )}

        <Group grow>
          <TextInput
            label="Street Address"
            placeholder="301 Memorial Medical Pkwy"
            {...form.getInputProps('street')}
            required
          />
          <TextInput
            label="City"
            placeholder="Daytona Beach"
            {...form.getInputProps('city')}
            required
          />
        </Group>
        <Group grow>
          <Select
            label="State"
            placeholder="Select state"
            data={states}
            {...form.getInputProps('state')}
            required
            searchable
          />
          <InputBase
            label="ZIP Code"
            placeholder="00000"
            component={IMaskInput}
            mask={[{ mask: '00000' }, { mask: '00000-0000' }]}
            {...form.getInputProps('zip')}
            required
          />
        </Group>

        <Group grow>
          <InputBase
            label="Fax"
            placeholder="(000) 000-0000"
            component={IMaskInput}
            mask="(000) 000-0000"
            value={form.values.fax}
            onAccept={(value) => form.setFieldValue('fax', value)}
            error={form.errors.fax}
            required
          />
          <InputBase
            label="Phone (optional)"
            placeholder="(000) 000-0000"
            component={IMaskInput}
            mask="(000) 000-0000"
            value={form.values.phone}
            onAccept={(value) => form.setFieldValue('phone', value)}
            error={form.errors.phone}
          />
        </Group>

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" loading={saving}>
            Add Facility
          </Button>
        </Group>
      </Stack>
    </form>
  )
}
